<template>
  <div class="shopee-report-page shopee-profit-loss-page">
    <KTCodePreview :title="'Báo cáo — Lãi lỗ đơn sàn'">
      <template #preview>
        <b-card class="mb-3 shopee-report-filter-card">
          <h6 class="text-muted mb-3">Bộ lọc</h6>
          <b-row align-v="start" class="shopee-report-filter-row mb-0">
            <b-col cols="12" md="3">
              <b-form-group label="Từ ngày" label-for="pl-df">
                <b-form-input id="pl-df" v-model="dateFrom" type="date" size="sm" />
              </b-form-group>
            </b-col>
            <b-col cols="12" md="3">
              <b-form-group label="Đến ngày" label-for="pl-dt">
                <b-form-input id="pl-dt" v-model="dateTo" type="date" size="sm" />
              </b-form-group>
            </b-col>
            <b-col cols="12" md="4" class="shopee-report-select-conn-col">
              <b-form-group label="Gian hàng" label-for="pl-conn">
                <b-form-select
                  id="pl-conn"
                  v-model="selectedConnectionId"
                  class="select-style"
                  :options="shopOptions"
                  size="sm"
                  :disabled="loadingConnections"
                >
                  <template #first>
                    <b-form-select-option :value="null">
                      {{ connections.length === 0 ? '(Chưa có kết nối)' : 'Tất cả kết nối' }}
                    </b-form-select-option>
                  </template>
                </b-form-select>
              </b-form-group>
            </b-col>
            <b-col cols="12" md="2">
              <b-form-group label="Số dòng / trang" label-for="pl-lim">
                <b-form-select
                  id="pl-lim"
                  v-model="limit"
                  class="select-style"
                  :options="limitOptions"
                  size="sm"
                  @change="onLimitChange"
                />
              </b-form-group>
            </b-col>
          </b-row>
          <div class="shopee-report-filter-actions d-flex flex-wrap align-items-center">
            <b-button-group size="sm" class="mr-2 mb-2">
              <b-button
                v-for="tab in viewTabs"
                :key="tab.key"
                :variant="view === tab.key ? 'primary' : 'outline-primary'"
                @click="switchView(tab.key)"
              >
                {{ tab.label }}
              </b-button>
            </b-button-group>
            <b-button variant="primary" size="sm" :disabled="!canSubmit" @click="reload">
              <b-spinner v-if="loading" small class="mr-1"></b-spinner>
              Tải dữ liệu
            </b-button>
          </div>
        </b-card>

        <b-alert v-if="profitNote" variant="info" show class="small">{{ profitNote }}</b-alert>
        <b-alert v-if="metaDateLine" variant="secondary" show class="small">{{ metaDateLine }}</b-alert>
        <b-alert v-if="error" variant="danger" show>{{ error }}</b-alert>

        <b-card v-if="summary && items.length" class="mb-3 reconciliation-table-shell" body-class="py-3">
          <h6 class="text-muted mb-3">Tổng kết kỳ</h6>
          <b-row>
            <b-col
              v-for="cell in summaryCells"
              :key="cell.label"
              cols="6"
              md="3"
              class="mb-2"
            >
              <div class="text-muted small">{{ cell.label }}</div>
              <div class="font-weight-bold">{{ cell.value }}</div>
            </b-col>
          </b-row>
        </b-card>

        <div class="reconciliation-table-shell">
          <b-table
            v-if="items.length > 0 || loading"
            striped
            hover
            responsive
            class="reconciliation-table"
            :busy="loading"
            :fields="activeFields"
            :items="items"
            show-empty
            empty-text="Không có dữ liệu"
          >
            <template #cell(invoiceAmount)="d">
              <span class="text-right d-block reconciliation-num">{{ fmtMoney(d.value) }}</span>
            </template>
            <template #cell(revenue)="d">
              <span class="text-right d-block reconciliation-num">{{ fmtMoney(d.value) }}</span>
            </template>
            <template #cell(platformFees)="d">
              <span class="text-right d-block reconciliation-num">{{ fmtMoney(d.value) }}</span>
            </template>
            <template #cell(cogs)="d">
              <span class="text-right d-block reconciliation-num">{{ fmtMoney(d.value) }}</span>
            </template>
            <template #cell(profit)="d">
              <span class="text-right d-block reconciliation-num">{{ fmtMoney(d.value) }}</span>
            </template>
            <template #cell(netRevenue)="d">
              <span class="text-right d-block reconciliation-num">{{ fmtMoney(d.value) }}</span>
            </template>
            <template #cell(orderId)="d">
              <router-link
                v-if="d.value"
                :to="{ name: 'edit-order', params: { id: d.value } }"
                class="order-id-link"
              >
                {{ d.value }}
              </router-link>
              <span v-else>—</span>
            </template>
            <template #cell(quantitySold)="d">{{ fmtInt(d.value) }}</template>
          </b-table>
        </div>

        <div v-if="totalRows > limit" class="d-flex justify-content-end mt-2">
          <b-pagination
            v-model="page"
            :total-rows="totalRows"
            :per-page="limit"
            align="center"
            class="custom-pagination mb-0"
            @input="fetchReport"
          />
        </div>
      </template>
    </KTCodePreview>
  </div>
</template>

<script>
  import KTCodePreview from '@/view/content/CodePreview.vue'
  import { SET_BREADCRUMB } from '@/core/services/store/modules/breadcrumbs.module'
  import { RepositoryFactory } from '@/repositories/RepositoryFactory'
  import { formatVnd, isoDateMinusDays, isoToday } from './formatters'

  const shopeeAdminRepo = RepositoryFactory.get('shopeeAdmin')
  const platformRepo = RepositoryFactory.get('platformIntegration')

  const ORDER_FIELDS = [
    { key: 'externalOrderId', label: 'Mã đơn sàn' },
    { key: 'orderId', label: 'ID đơn' },
    { key: 'orderDate', label: 'Ngày đơn' },
    { key: 'invoiceAmount', label: 'Số tiền HĐ', tdClass: 'text-right', thClass: 'text-right' },
    { key: 'revenue', label: 'Thực thu', tdClass: 'text-right', thClass: 'text-right' },
    { key: 'platformFees', label: 'Phí sàn', tdClass: 'text-right', thClass: 'text-right' },
    { key: 'cogs', label: 'Giá vốn', tdClass: 'text-right', thClass: 'text-right' },
    { key: 'profit', label: 'Lãi lỗ', tdClass: 'text-right', thClass: 'text-right' },
  ]

  const IMEI_FIELDS = [
    { key: 'imei', label: 'IMEI' },
    { key: 'productName', label: 'Tên hàng' },
    { key: 'productCode', label: 'Mã SP' },
    { key: 'externalOrderId', label: 'Mã đơn sàn' },
    { key: 'orderId', label: 'ID đơn' },
    { key: 'revenue', label: 'Doanh thu', tdClass: 'text-right', thClass: 'text-right' },
    { key: 'cogs', label: 'Giá vốn', tdClass: 'text-right', thClass: 'text-right' },
    { key: 'profit', label: 'Lãi lỗ', tdClass: 'text-right', thClass: 'text-right' },
  ]

  const PRODUCT_FIELDS = [
    { key: 'productCode', label: 'Mã SP' },
    { key: 'productName', label: 'Tên hàng' },
    { key: 'quantitySold', label: 'SL bán', tdClass: 'text-right', thClass: 'text-right' },
    { key: 'cogs', label: 'Giá vốn', tdClass: 'text-right', thClass: 'text-right' },
    { key: 'netRevenue', label: 'DT thuần', tdClass: 'text-right', thClass: 'text-right' },
    { key: 'profit', label: 'Lãi lỗ', tdClass: 'text-right', thClass: 'text-right' },
  ]

  export default {
    name: 'ShopeeReportProfitLoss',
    components: { KTCodePreview },
    data() {
      return {
        view: 'order',
        viewTabs: [
          { key: 'order', label: 'Theo đơn' },
          { key: 'imei', label: 'Theo IMEI' },
          { key: 'product', label: 'Theo hàng hóa' },
        ],
        dateFrom: '',
        dateTo: '',
        page: 1,
        limit: 20,
        limitOptions: [
          { value: 10, text: '10' },
          { value: 20, text: '20' },
          { value: 50, text: '50' },
          { value: 100, text: '100' },
        ],
        loading: false,
        loadingConnections: false,
        connections: [],
        selectedConnectionId: null,
        error: null,
        meta: null,
        items: [],
        summary: null,
        pagination: null,
      }
    },
    computed: {
      shopOptions() {
        return this.connections.map((c) => ({
          value: c.id,
          text: c.shopName || `Shop #${c.id}`,
        }))
      },
      canSubmit() {
        return !!(this.dateFrom && this.dateTo && !this.loading)
      },
      profitNote() {
        return this.meta?.profitNote || ''
      },
      metaDateLine() {
        if (!this.meta?.dateFrom || !this.meta?.dateTo) return ''
        return `${this.meta.dateFrom} → ${this.meta.dateTo}`
      },
      activeFields() {
        if (this.view === 'imei') return IMEI_FIELDS
        if (this.view === 'product') return PRODUCT_FIELDS
        return ORDER_FIELDS
      },
      totalRows() {
        const n = Number(this.pagination?.total)
        return Number.isNaN(n) ? 0 : n
      },
      summaryCells() {
        const s = this.summary || {}
        if (this.view === 'order') {
          return [
            { label: 'Số đơn', value: this.fmtInt(s.orderCount) },
            { label: 'Thực thu', value: this.fmtMoney(s.totalRevenue) },
            { label: 'Giá vốn', value: this.fmtMoney(s.totalCogs) },
            { label: 'Lãi lỗ', value: this.fmtMoney(s.totalProfit) },
          ]
        }
        if (this.view === 'imei') {
          return [
            { label: 'Số IMEI', value: this.fmtInt(s.imeiCount) },
            { label: 'Doanh thu', value: this.fmtMoney(s.totalRevenue) },
            { label: 'Giá vốn', value: this.fmtMoney(s.totalCogs) },
            { label: 'Lãi lỗ', value: this.fmtMoney(s.totalProfit) },
          ]
        }
        return [
          { label: 'Số SP', value: this.fmtInt(s.productCount) },
          { label: 'SL bán', value: this.fmtInt(s.quantitySold) },
          { label: 'DT thuần', value: this.fmtMoney(s.totalRevenue) },
          { label: 'Lãi lỗ', value: this.fmtMoney(s.totalProfit) },
        ]
      },
    },
    async created() {
      this.$store.dispatch(SET_BREADCRUMB, [
        { title: 'Kênh bán', route: '/platform-integration/orders' },
        { title: 'Báo cáo', route: '/platform-integration/reports/profit-loss' },
        { title: 'Báo cáo lãi lỗ' },
      ])
      this.dateTo = isoToday()
      this.dateFrom = isoDateMinusDays(29)
      await this.fetchConnections()
      await this.fetchReport()
    },
    methods: {
      fmtMoney(value) {
        if (value === null || value === undefined || value === '') return '—'
        return formatVnd(value)
      },
      fmtInt(v) {
        if (v == null || v === '') return '—'
        const n = Number(v)
        if (Number.isNaN(n)) return String(v)
        return new Intl.NumberFormat('vi-VN').format(n)
      },
      switchView(key) {
        if (this.view === key) return
        this.view = key
        this.page = 1
        this.fetchReport()
      },
      onLimitChange() {
        this.page = 1
      },
      reload() {
        this.page = 1
        this.fetchReport()
      },
      async fetchConnections() {
        try {
          this.loadingConnections = true
          const res = await platformRepo.getConfigs({ platform: 'shopee' })
          this.connections = (res.items || []).filter((c) => c.status === 'connected')
          if (this.connections.length === 1 && this.selectedConnectionId == null) {
            this.selectedConnectionId = this.connections[0].id
          }
        } catch (e) {
          console.error(e)
        } finally {
          this.loadingConnections = false
        }
      },
      async fetchReport() {
        this.error = null
        if (!this.dateFrom || !this.dateTo) {
          this.error = 'Vui lòng chọn đủ từ ngày và đến ngày.'
          return
        }
        try {
          this.loading = true
          const params = {
            dateFrom: this.dateFrom,
            dateTo: this.dateTo,
            view: this.view,
            page: this.page,
            limit: this.limit,
          }
          if (this.selectedConnectionId != null) {
            params.connectionId = this.selectedConnectionId
          }
          const raw = await shopeeAdminRepo.getReportProfitLoss(params)
          this.meta = raw?.meta ?? null
          this.items = Array.isArray(raw?.items) ? raw.items : []
          this.summary = raw?.summary ?? null
          this.pagination = raw?.pagination ?? null
        } catch (e) {
          console.error(e)
          this.items = []
          this.summary = null
          this.error =
            e.response?.data?.error || e.response?.data?.message || 'Không tải được báo cáo.'
        } finally {
          this.loading = false
        }
      },
    },
  }
</script>

<style lang="scss" scoped>
  @import './shopee-report-filters.scss';
  @import '../styles/shopee-platform-filter-skin.mixin.scss';

  .shopee-profit-loss-page {
    .reconciliation-table {
      font-size: 0.875rem;
    }
  }
</style>
